import { IBetPageApi, IBetAppApi, IBetWS } from '@/types/bet';
import { BetType, MarketType, Teamwhere, TradingType, WL } from '@/constants';
import { BetItem } from '@/models/bet';

export class BetFactory {
    static createFromPageApi(data: IBetPageApi): BetItem {
        const bet = new BetItem();
        bet.botId = data.bot_id;
        bet.bookmakerId = data.site_id;
        bet.createdAt = data.created_at;
        bet.odds = data.odds;
        bet.handicap = data.handicap;
        bet.type = data.type as BetType;
        bet.marketTypeId = data.market_type_id as MarketType;
        bet.eventMarketId = data.event_market_id;
        bet.jollywhere = data.jollywhere;
        bet.jollywhereClose = data.jollywhere_close;
        bet.teamwhere = data.teamwhere;
        bet.periodTypeId = data.period_type_id;
        bet.homeWinPercClose = data.home_win_perc_close;
        bet.awayWinPercClose = data.away_win_perc_close;
        if (data.score) {
            const [home, away] = data.score.split('-');
            bet.scoreHome = Number(home);
            bet.scoreAway = Number(away);
        } else {
            bet.scoreHome = bet.scoreAway = null;
        }
        bet.time = data.time;
        bet.timeH = data.time_h;
        bet.timeMin = data.time_min;
        bet.situation = data.situation;
        bet.hdpVMainOpen = data.hdp_v_main_open;
        bet.ouVMainOpen = data.ou_v_main_open;
        bet.hdpVMainClose = data.hdp_v_main_close;
        bet.ouVMainClose = data.ou_v_main_close;
        bet.wldHomeClose = data.wld_home_close;
        bet.wldAwayClose = data.wld_away_close;
        bet.wldDrawClose = data.wld_draw_close;
        bet.stake = data.stake;
        bet.stakeRequest = data.stake_request;
        bet.balance = data.balance;
        bet.wl = data.result as WL;
        bet.statusId = data.status_id;
        bet.redHome = data.red_home;
        bet.redAway = data.red_away;
        bet.topup = data.topup;
        bet.correctScore = data.correct_score;
        bet.isLive = data.is_live;
        bet.tradingTypeId = data.trading_type_id as TradingType;
        bet.conditions = data.conditions;
        bet.id = data.id;
        bet.possessionTimeHome = data.possession_time_home;
        bet.possessionTimeAway = data.possession_time_away;
        bet.attacksHome = data.attacks_home;
        bet.attacksAway = data.attacks_away;
        bet.dangerousAttacksHome = data.dangerous_attacks_home;
        bet.dangerousAttacksAway = data.dangerous_attacks_away;
        bet.shotsHome = data.shots_home;
        bet.shotsAway = data.shots_away;
        bet.shotsOnTargetHome = data.shots_on_target_home;
        bet.shotsOnTargetAway = data.shots_on_target_away;
        bet.hitWoodworkHome = data.hit_woodwork_home;
        bet.hitWoodworkAway = data.hit_woodwork_away;
        bet.foulsHome = data.fouls_home;
        bet.foulsAway = data.fouls_away;
        bet.marginPerc = data.margin_perc;
        bet.cornersHome = data.corners_home;
        bet.cornersAway = data.corners_away;
        bet.orderCreatedAt = data.order_created_at;
        bet.orderId = data.order_id;

        if (data.score_ft) {
            const [home, away] = data.score_ft.split('-');
            bet.scoreHomeFt = Number(home);
            bet.scoreAwayFt = Number(away);
        } else {
            bet.scoreHomeFt = bet.scoreAwayFt = null;
        }
        if (data.score_ht) {
            const [home, away] = data.score_ht.split('-');
            bet.scoreHomeHt = Number(home);
            bet.scoreAwayHt = Number(away);
        } else {
            bet.scoreHomeHt = bet.scoreAwayHt = null;
        }
        bet.eventStartedAt = data.event_started_at;
        bet.customEventId = data.custom_event_id;
        bet.cornersHomeFt = data.corners_home_ft;
        bet.cornersAwayFt = data.corners_away_ft;
        bet.cornersHomeHt = data.corners_home_ht;
        bet.cornersAwayHt = data.corners_away_ht;
        bet.customTeamHomeName = data.custom_team_home_name;
        bet.customTeamAwayName = data.custom_team_away_name;
        bet.botName = data.bot_name;
        bet.botDeletedAt = data.bot_deleted_at;
        bet.customLeagueId = data.custom_league_id;
        bet.customLeagueName = data.custom_league_name;
        bet.iso3 = data.iso3;
        bet.ip = data.ip;
        bet.deviceTypeId = data.device_type_id;

        return bet;
    }

    static createFromAppApi(data: IBetAppApi): BetItem {
        const bet = new BetItem();
        bet.id = data.internal_id;
        bet.botId = data.botId;
        bet.orderId = data.bet_order_id;
        bet.orderCreatedAt = data.created_at;
        bet.bookmakerId = data.site_id;
        bet.createdAt = data.created_at;
        bet.statusId = data.status_id;
        bet.eventStartedAt = data.event_started_at;
        bet.customEventId = data.custom_event_id;
        bet.odds = data.odds;
        bet.handicap = data.handicap;
        bet.type = data.type as BetType;
        bet.marketTypeId = data.market_type_id as MarketType;
        bet.eventMarketId = data.event_market_id;
        bet.jollywhere = data.jollywhere;
        bet.jollywhereClose = data.jollywhere_close;
        bet.teamwhere = data.teamwhere as Teamwhere;
        bet.periodTypeId = data.period_type_id;
        if (data.score) {
            const [home, away] = data.score.split('-');
            bet.scoreHome = Number(home);
            bet.scoreAway = Number(away);
        } else {
            if (data.is_live) {
                bet.scoreHome = bet.scoreAway = null;
            } else {
                bet.scoreHome = bet.scoreAway = 0;
            }
        }
        if (data.score_ir_ht !== null) {
            const [home, away] = data.score_ir_ht.split('-');
            bet.scoreHomeIrHt = Number(home);
            bet.scoreAwayIrHt = Number(away);
        }
        if (data.corners_home_ir_ht !== null && data.corners_away_ir_ht !== null) {
            bet.cornersHomeIrHt = data.corners_home_ir_ht;
            bet.cornersAwayIrHt = data.corners_away_ir_ht;
        }
        bet.time = data.time;
        bet.timeH = data.time_h;
        bet.timeMin = data.time_min;
        bet.situation = data.situation;
        bet.stake = data.stake;
        bet.stakeRequest = data.stake_request;
        bet.balance = data.balance;
        bet.correctScore = data.correct_score;
        bet.isLive = data.is_live;
        bet.tradingTypeId = data.trading_type_id as TradingType;
        bet.conditions = '';
        bet.marginPerc = data.margin_perc;
        bet.cornersHome = data.corners_home;
        bet.cornersAway = data.corners_away;
        bet.customTeamHomeName = data.team_home_name;
        bet.customTeamAwayName = data.team_away_name;
        bet.customLeagueId = data.league_id;
        bet.customLeagueName = data.league_name;
        bet.iso3 = data.iso3;

        return bet;
    }

    static createFromWS(data: IBetWS): BetItem {
        const bet = new BetItem();
        bet.id = data.internal_id;
        bet.botId = data.botId;
        if (bet.botId) {
            bet.botName = data.name;
        }
        bet.orderId = data.betOrderInternalId;
        bet.orderCreatedAt = data.created_at;
        bet.bookmakerId = data.site_id;
        bet.createdAt = data.created_at;
        bet.statusId = data.status_id;
        bet.eventStartedAt = data.event_started_at;
        bet.customEventId = data.custom_event_id;
        bet.odds = data.odds;
        bet.handicap = data.handicap;
        bet.type = data.type as BetType;
        bet.marketTypeId = data.market_type_id as MarketType;
        bet.eventMarketId = data.event_market_id;
        bet.jollywhere = data.jollywhere;
        bet.jollywhereClose = data.jollywhere_close;
        bet.teamwhere = data.teamwhere as Teamwhere;
        bet.periodTypeId = data.period_type_id;
        if (data.score) {
            const [home, away] = data.score.split('-');
            bet.scoreHome = Number(home);
            bet.scoreAway = Number(away);
        } else {
            if (data.is_live) {
                bet.scoreHome = bet.scoreAway = null;
            } else {
                bet.scoreHome = bet.scoreAway = 0;
            }
        }
        bet.time = data.time;
        bet.timeH = data.time_h;
        bet.timeMin = data.time_min;
        bet.situation = data.situation;
        bet.stake = data.stake;
        bet.stakeRequest = data.stake_request;
        bet.correctScore = data.correct_score;
        bet.isLive = data.is_live;
        bet.tradingTypeId = data.trading_type_id as TradingType;
        bet.conditions = '';
        bet.marginPerc = data.margin_perc;
        bet.cornersHome = data.corners_home;
        bet.cornersAway = data.corners_away;
        bet.customTeamHomeName = data.team_home_name;
        bet.customTeamAwayName = data.team_away_name;
        bet.customLeagueId = data.league_id;
        bet.customLeagueName = data.league_name;
        bet.iso3 = ''; // todo need to change it

        return bet;
    }
}
